import React from 'react';
import { Box, Typography } from '@mui/material';

import useIntersectionObserver from '../../../hooks/useIntersectionObserver';
import THEME from '../../../constants/theme';

/**
 * PageHeader component for displaying page titles and optional subtitles
 *
 * @param {Object} props - Component props
 * @param {string} props.id - Optional id for the header element (for scroll navigation)
 * @param {string} props.title - Main header title
 * @param {string} props.subtitle - Optional subtitle
 * @param {string} props.align - Text alignment (left, center, right)
 * @param {Object} props.sx - Additional MUI styling
 * @returns {React.Component} PageHeader component
 */
const PageHeader = ({
  id,
  title,
  subtitle,
  align = 'left',
  sx = {},
  headingTag = 'h2',
  subtitleTag = 'h1'
}) => {
  const headerRef = React.useRef(null);
  const isVisible = useIntersectionObserver(headerRef);

  return (
    <Box
      id={id}
      ref={headerRef}
      sx={{
        mb: subtitle ? THEME.SPACING.MEDIUM : THEME.SPACING.XL,
        textAlign: align,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity ${THEME.TRANSITIONS.SLOW} ease, transform ${THEME.TRANSITIONS.SLOW} ease`,
        ...sx
      }}
    >
      <Typography
        variant='h2'
        component={headingTag || 'h2'}
        sx={{
          color: THEME.COLORS.NEUTRAL_BLACK,
          fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
          lineHeight: 1.2,
          marginBottom: subtitle ? THEME.SPACING.SMALL : 0,
          fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
          textAlign: align
        }}
      >
        {title}
      </Typography>

      {subtitle && (
        <Typography
          variant='h6'
          component={subtitleTag || 'h1'}
          sx={{
            color: THEME.COLORS.NEUTRAL_GRAY_DARK,
            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
            fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.7rem' },
            lineHeight: 1.5,
            marginTop: 0,
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            textAlign: align
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

export default PageHeader;
